import {Component} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, UntypedFormControl, Validators} from '@angular/forms';
import {JSONSchema7} from 'json-schema';
import {TreeNode} from 'primeng/api';
import {firstValueFrom} from 'rxjs';
import {ECpmAEPluginFunctionTypeApiEnum} from '../../../../gen/cpm-ae-api-ts/src/enums/ae-global/cpmae-plugins.api-enums';
import {
    CpmAEAllPluginSpecApiSchema,
    CpmAEPluginFunctionApiSchema,
    CpmAEPluginSpecApiSchema
} from '../../../../gen/cpm-ae-api-ts/src/schemas/ae-global/cpmae-plugins.api-schemas';
import {
    ECpmCTaskStatusApiEnum,
    ECpmCTaskTypeApiEnum
} from '../../../../gen/cpm-c-api-ts/src/enums/c-customer/cpmc-task.api-enums';
import {CpmCTaskApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-task.api-model';
import {
    CpmCTaskCreateApiSchema,
    CpmCTaskInputOutputMappingApiSchema,
    CpmCTaskUpdateApiSchema
} from '../../../../gen/cpm-c-api-ts/src/schemas/c-customer/cpmc-task.api-schemas';
import {CpmCTaskApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-task.api-service';
import {IBaseCRUModalComponent} from '../../../../sdk/src/components/ibase-cru-modal/ibase-cru-modal.component';
import {EDialogOpenMode} from '../../../../sdk/src/enums/dialog-open-mode.enum';
import {
    ConfirmationModalComponent,
    ConfirmationModalConfig
} from '../../../../sdk/src/modals/confirm-modal/confirm-modal.component';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {SdkImportsModule} from '../../../../sdk/src/sdk-imports';
import {DateUtils} from '../../../../sdk/src/utils/date.utils';
import {JsonUtils} from '../../../../sdk/src/utils/json.utils';
import {TreeUtils} from '../../../../sdk/src/utils/tree.utils';
import {TaskTreeViewExtension} from '../../api-extensions/views/task-tree.view-extension';
import {JobsSpecificationCaching} from '../../caching/jobs-specification.caching';
import {
    ActionJobsFunctionIoEditor,
    ActionJobsFunctionIoEditorChangeEvent
} from '../../components/action-jobs-function-io-editor/action-jobs-function-io-editor.component';
import {
    EObjectStoreEntriesLevel,
    ObjectStoreEntriesTransientCache
} from '../../transient-cache/object-store-entries.transient-cache';
import {ActionJobsUtils} from '../../utils/action-jobs.utils';
import {ReferenceExpressionUtils} from '../../utils/reference-expression.utils';
import {TaskOptionDisplay, TaskOptionsUtils} from '../../utils/task-options.utils';
import {TaskUtils} from '../../utils/task.utils';

export class NewEditTaskModalConfig {
    constructor(
        public existing?: CpmCTaskApiModel | TaskTreeViewExtension,
        public initialTaskType?: ECpmCTaskTypeApiEnum,
        public isConvertingType?: boolean
    ) {}
}

export class NewEditTaskModalResult {
    constructor(public item?: CpmCTaskApiModel) {}
}

/**
 * New/Edit Task modal
 */
@Component({
    selector: 'app-new-edit-task-modal',
    templateUrl: './new-edit-task-modal.component.html',
    imports: [ReactiveFormsModule, PrimeNgImportsModule, SdkImportsModule, ActionJobsFunctionIoEditor]
})
export class NewEditTaskModalComponent extends IBaseCRUModalComponent<
    CpmCTaskApiModel,
    NewEditTaskModalComponent,
    NewEditTaskModalConfig,
    NewEditTaskModalResult
> {
    protected readonly ECpmCTaskTypeApiEnum = ECpmCTaskTypeApiEnum;
    protected readonly ECpmCTaskStatusApiEnum = ECpmCTaskStatusApiEnum;
    protected readonly TaskUtils = TaskUtils;

    // ===============================================================
    // ===============================================================

    protected _item?: CpmCTaskApiModel;
    protected _stepperStep = 1;
    protected _stepperMax = 2;

    // ===============================================================
    // ===============================================================

    protected taskType?: ECpmCTaskTypeApiEnum;
    protected tasksTreeNodes: TreeNode<TaskTreeViewExtension>[] = [];
    protected taskGroupsTreeNodes: TreeNode<TaskTreeViewExtension>[] = [];
    protected options: TaskOptionDisplay[] = [];
    protected dependenciesForm!: FormControl<TreeNode<CpmCTaskApiModel>[] | null>;

    // ===============================================================
    // ===============================================================

    protected _functionsTree: TreeNode<CpmAEPluginSpecApiSchema | CpmAEPluginFunctionApiSchema>[] = [];
    protected _selectedPlugin?: CpmAEPluginSpecApiSchema;
    protected _selectedPluginFunction?: CpmAEPluginFunctionApiSchema;
    protected _inputsMappings: Record<string, CpmCTaskInputOutputMappingApiSchema> = {};
    protected _inputsMappingValid = false;
    protected _outputsMappings: Record<string, CpmCTaskInputOutputMappingApiSchema> = {};
    protected _outputsMappingValid = false;
    protected _objectStoreEntriesTransientCache?: ObjectStoreEntriesTransientCache;
    protected _initialInputsMappings?: Record<string, CpmCTaskInputOutputMappingApiSchema>;

    // ===============================================================
    // ===============================================================

    private _firstUpdateWithType = true;
    private _specificationAppDetails?: CpmAEAllPluginSpecApiSchema;

    protected get currentTaskLabel(): string | undefined {
        const formTaskName = this.form?.controls['name']?.value?.trim();
        return formTaskName || this._item?.name;
    }

    protected readonly taskFunctionChangeFirstSentence = `You are about to change the function of a task that has already been executed.`;
    protected readonly taskInputChangeFirstSentence = `You are about to change an input value of a task that has already been executed.`;
    protected readonly resetWarningSentence =
        'Doing so will reset the status of this task, already executed dependent tasks, and already executed tasks contained in dependent groups.';
    protected readonly milestoneSentence = 'Adding a function to this milestone task will remove its milestone status.';
    protected readonly confirmationSentence = 'Do you confirm?';

    // ===============================================================
    // ===============================================================

    protected _inputsDirty = false;
    protected _functionDirty = false;

    /**
     * Determines whether a warning should be shown when the input of the task is changed.
     */
    protected get shouldWarnResetOnChange(): boolean {
        return (
            this.dialogOpenMode === EDialogOpenMode.EDIT &&
            !!this._item &&
            TaskUtils.isExecutedStatus(this._item.status)
        );
    }

    /**
     * Determines whether a warning should be shown when the input of the task is changed and the task is dirty.
     */
    protected get shouldWarnResetOnChangeAndDirty(): boolean {
        return this.shouldWarnResetOnChange && (this._inputsDirty || this._functionDirty);
    }

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.configure('Task', (model: CpmCTaskApiModel) => model.name ?? '<Unknown task>');
        this.canSubmitExtra = () => {
            // Only validate IO mappings if function is selected
            if (this.taskType === ECpmCTaskTypeApiEnum.SMART) {
                const functionControl = this.form?.controls['function'];
                const functionSelected = functionControl?.value;
                const selectedOptions: string[] = this.form?.controls['options']?.value || [];
                const requiresManualInput = selectedOptions.includes('smart_requires_manual_input');

                // If smart_requires_manual_input is checked, a function MUST be selected
                if (requiresManualInput && !functionSelected) {
                    return false;
                }

                if (functionSelected && this._objectStoreEntriesTransientCache?.isInitialized) {
                    // If requires manual input is checked, allow empty inputs/outputs
                    if (requiresManualInput) {
                        return true;
                    }

                    // Otherwise, require valid inputs and outputs
                    return this._inputsMappingValid && this._outputsMappingValid;
                }
                // If no function selected, it's valid (will become SIMPLE task)
                return true;
            }
            return true;
        };

        this.config.width = '40rem';
        this.contentMinHeight = '20rem';
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling is done in the parent)
     */
    protected async onReady(): Promise<void> {
        this.dependenciesForm = new FormControl<TreeNode<CpmCTaskApiModel>[] | null>(null, []);
        this.form = new FormGroup({
            parent_uuid: new FormControl<TreeNode<CpmCTaskApiModel> | null>(null, []),
            name: new FormControl<string | null>(null, [Validators.required]),
            due_date: new FormControl<string | null>(null, []),
            start_date: new FormControl<string | null>(null, []),
            end_date: new FormControl<string | null>(null, []),
            status: new FormControl<ECpmCTaskStatusApiEnum | null>(ECpmCTaskStatusApiEnum.CREATED, []),
            brief: new FormControl<string | null>(null, [Validators.maxLength(500)]),
            description: new FormControl<string | null>(null, []),
            options: new UntypedFormControl([], []),
            dependencies: this.dependenciesForm
        });

        await this.parallelSequencedAsyncCritical([
            {
                m: 'Loading the task...',
                f: async (): Promise<void> => {
                    if (this.dialogOpenMode === EDialogOpenMode.NEW) return;

                    this._item = await firstValueFrom(
                        CpmCTaskApiService.getTaskByUUID(this.config.data?.extended?.existing?.uuid ?? '')
                    );
                }
            },
            {
                m: 'Loading task groups...',
                f: async (): Promise<void> => {
                    // Fetch all groups, without pagination
                    const tasks = (
                        await firstValueFrom(CpmCTaskApiService.getTaskTree({}, CpmCTaskApiService.dPaginatorConfig))
                    ).data;

                    // Building tree
                    this.tasksTreeNodes = TaskUtils.buildTaskTree(tasks);
                    TaskUtils.sortTaskTree(this.tasksTreeNodes);
                    TaskUtils.addNumbersToTaskTree(this.tasksTreeNodes, 'label');

                    // Clone before applying specific modifications to each tree
                    this.taskGroupsTreeNodes = TreeUtils.cloneTree(this.tasksTreeNodes);

                    if (this.dialogOpenMode !== EDialogOpenMode.CLONE && this.config.data?.extended?.existing) {
                        // Disable self for dependency selection
                        TreeUtils.disableCurrentTreeNode(this.tasksTreeNodes, this.config.data.extended.existing.uuid);

                        // Disable self for parent group &  non group tasks
                        TreeUtils.pruneTree(
                            this.taskGroupsTreeNodes,
                            (x) =>
                                x.key === this.config.data!.extended!.existing?.uuid ||
                                x.data?.task_type !== ECpmCTaskTypeApiEnum.GROUP
                        );
                    } else {
                        // Prune non group tasks
                        TreeUtils.pruneTree(
                            this.taskGroupsTreeNodes,
                            (x) => x.data?.task_type !== ECpmCTaskTypeApiEnum.GROUP
                        );
                    }
                }
            },
            {
                m: 'Loading functions...',
                f: async (): Promise<void> => {
                    this._functionsTree = await ActionJobsUtils.getFunctionsTree(ECpmAEPluginFunctionTypeApiEnum.TASK);
                }
            }
        ]);

        // Set initial task type and update dialog AFTER loading is complete
        if (this.dialogOpenMode === EDialogOpenMode.NEW && this.config.data?.extended?.initialTaskType) {
            this.taskType = this.config.data.extended.initialTaskType;
            await this.updateDialog();
        } else if (this.dialogOpenMode !== EDialogOpenMode.NEW) {
            if (this.dialogOpenMode !== EDialogOpenMode.CLONE) {
                this.config.header += `: ${this._item!.name}`;
            }

            this.loadingStr = 'Loading the task...';
            this.taskType = this._item?.task_type;
            await this.updateDialog();
            this.patchForm();
        }
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * Path form content/value
     */
    protected patchForm() {
        const toPatch = {...this._item!};
        delete toPatch.parent_uuid;
        delete toPatch.due_date;
        delete toPatch.start_date;
        delete toPatch.end_date;
        this.form.patchValue(toPatch);

        if (this._item?.due_date) {
            this.form.patchValue({due_date: new Date(this._item.due_date)});
        }

        if (this._item?.start_date) {
            this.form.patchValue({start_date: new Date(this._item.start_date)});
        }

        if (this._item?.end_date) {
            this.form.patchValue({end_date: new Date(this._item.end_date)});
        }

        const taskOptions = TaskOptionsUtils.getOptionsForTask(this._item!);
        const selectedTags = taskOptions.filter((opt) => opt.checked).map((opt) => opt.tag);
        this.form.patchValue({options: selectedTags});

        if (this._item!.parent_uuid) {
            const parentNode = TreeUtils.findTreeNodeByKey(this.taskGroupsTreeNodes, this._item!.parent_uuid);
            if (parentNode) {
                this.form.controls['parent_uuid'].setValue(parentNode);
            }
        }

        this.patchFormDependencies();
        this.patchFormPluginFunction();
    }

    /**
     * Patch the dependencies form control from the existing item's dependency relationships
     */
    private patchFormDependencies(): void {
        if (!this._item!.dependency_relationships?.length) {
            return;
        }
        const dependencyNodes = this._item!.dependency_relationships.map((model) =>
            TreeUtils.findTreeNodeByKey(this.tasksTreeNodes, model.depends_on_task_uuid)
        ).filter((node) => node !== null);

        if (dependencyNodes.length > 0) {
            this.form.controls['dependencies'].setValue(dependencyNodes);
        }
    }

    /**
     * Patch the function control and IO mappings from the existing item's plugin configuration
     */
    private patchFormPluginFunction(): void {
        const isConvertingType = this.config.data?.extended?.isConvertingType;

        if (!isConvertingType && this._item!.plugin_id && this._item!.plugin_function_id) {
            const foundFunction = this.getTaskFunction();

            if (foundFunction) {
                const functionControl = this.form.controls['function'];
                if (functionControl) {
                    functionControl.setValue(foundFunction);
                    this._selectedPlugin = foundFunction.parent?.data as CpmAEPluginSpecApiSchema;
                    this._selectedPluginFunction = foundFunction.data as CpmAEPluginFunctionApiSchema;
                }
            }

            this._inputsMappings =
                (this._item!.inputs_mapping as Record<string, CpmCTaskInputOutputMappingApiSchema> | undefined) ?? {};
            this._outputsMappings =
                (this._item!.outputs_mapping as Record<string, CpmCTaskInputOutputMappingApiSchema> | undefined) ?? {};

            this._initialInputsMappings = structuredClone(this._inputsMappings);
        }
    }

    /**
     * Update the dialog
     */
    protected async updateDialog() {
        if (this.taskType) {
            this.config.width = '60%';
            this.contentMinHeight = '40rem';
        } else {
            this.config.width = '40rem';
            this.contentMinHeight = '20rem';
        }

        this.updateDialogTitle();

        if (!this.taskType || !this.form) {
            return;
        }

        // Build a temporary task to compute available options for the current task type
        // Use actual task attributes if editing, otherwise use 0
        const tempTask = {task_type: this.taskType, attributes1: this._item?.attributes1 ?? 0} as CpmCTaskApiModel;
        this.options = TaskOptionsUtils.getOptionsForTask(tempTask);

        // Only run first-time setup (form controls, object store) once per task type selection
        if (this._firstUpdateWithType) {
            await this.initializeFirstTypeView();
        }
    }

    /**
     * Perform one-time initialisation when a task type is selected for the first time:
     * adds the function form control, sets the stepper max and initialises the object store cache
     */
    private async initializeFirstTypeView(): Promise<void> {
        if (this.taskType === ECpmCTaskTypeApiEnum.SMART || this.taskType === ECpmCTaskTypeApiEnum.SIMPLE) {
            this._stepperMax = 3;

            if (this.taskType === ECpmCTaskTypeApiEnum.SMART && this.form.controls['status']) {
                this.form.controls['status']?.disable();
            }

            if (!this.form.controls['function']) {
                const functionControl = new FormControl<TreeNode<
                    CpmAEPluginSpecApiSchema | CpmAEPluginFunctionApiSchema
                > | null>(null, []);
                this.form.addControl('function', functionControl);
                functionControl.valueChanges.subscribe(this.onFunctionChanged.bind(this));
            }

            if (!this._objectStoreEntriesTransientCache) {
                try {
                    this._objectStoreEntriesTransientCache = new ObjectStoreEntriesTransientCache(
                        this.globalsService.sessionService.getCustomer()!.uuid,
                        {
                            organizationUUID: this.globalsService.sessionService.getOrganization()?.uuid,
                            projectUUID: this.globalsService.sessionService.getProject()?.uuid,
                            serviceRequestUUID: this.globalsService.sessionService.getServiceRequest()?.uuid
                        }
                    );
                    await this._objectStoreEntriesTransientCache.init();
                } catch (error) {
                    console.error('ObjectStore initialization failed:', error);
                    this._objectStoreEntriesTransientCache = {
                        isInitialized: true
                    } as ObjectStoreEntriesTransientCache;
                }
            }
        }

        this._firstUpdateWithType = false;
    }

    /**
     * Update the title of the dialog
     */
    protected updateDialogTitle() {
        switch (this.dialogOpenMode) {
            case EDialogOpenMode.NEW:
                if (this.taskType === ECpmCTaskTypeApiEnum.GROUP) {
                    this.config.header = 'Creating Group';
                } else {
                    this.config.header = 'Creating Task';
                }
                break;

            case EDialogOpenMode.CLONE:
                if (this._item) {
                    this.config.header = `Cloning: ${this._item.name}`;
                }
                break;

            case EDialogOpenMode.EDIT:
                if (this._item) {
                    this.config.header = `Editing: ${this._item.name}`;
                }
                break;

            case EDialogOpenMode.READ_ONLY:
                if (this._item) {
                    this.config.header = `Viewing: ${this._item.name}`;
                }
                break;
        }
    }

    // ===============================================================
    // ===============================================================

    /**
     * When the function is changed
     */
    protected onFunctionChanged() {
        const currentFunctionValue = this.getTaskFunction();
        const newFunctionValue = this.form.controls['function']?.value;

        if (currentFunctionValue === newFunctionValue) {
            return;
        }

        const isMilestone = !!this._item && TaskUtils.isMilestone(this._item);
        const mustConfirmReset = this.shouldWarnResetOnChange;
        const mustConfirmMilestone = isMilestone && !!newFunctionValue;

        if (!mustConfirmReset && !mustConfirmMilestone) {
            this._proceedWithFunctionChange(newFunctionValue);
            return;
        }

        this.globalsService.dialogService
            .open(
                ConfirmationModalComponent,
                ConfirmationModalComponent.getDialogServiceParameters(
                    this.getTaskChangeConfirmation(false, true, mustConfirmMilestone)
                )
            )
            .onClose.subscribe((confirmed: boolean) => {
                if (!confirmed) {
                    this.form.controls['function']?.setValue(currentFunctionValue ?? null, {emitEvent: false});
                    return;
                }

                if (mustConfirmReset) {
                    this._inputsDirty = false;
                    this._functionDirty = true;
                }
                this._proceedWithFunctionChange(newFunctionValue);
            });
    }

    /**
     * Internal method to handle function change after milestone check
     */
    private _proceedWithFunctionChange(
        newFunctionValue: TreeNode<CpmAEPluginSpecApiSchema | CpmAEPluginFunctionApiSchema> | null
    ) {
        if (this._selectedPluginFunction != newFunctionValue?.data) {
            // Reset state
            this._inputsMappings = {};
            this._outputsMappings = {};
            this._initialInputsMappings = {};
            this._inputsMappingValid = false;
            this._outputsMappingValid = false;
        }

        // Change function
        this._selectedPlugin = newFunctionValue?.parent?.data as CpmAEPluginSpecApiSchema | undefined;
        this._selectedPluginFunction = newFunctionValue?.data as CpmAEPluginFunctionApiSchema | undefined;

        this.taskType = this.determineFinalTaskType();
        void this.updateDialog();
    }

    // ===============================================================
    // ===============================================================

    /**
     * On previous
     */
    public override onPrevious() {
        if (this._stepperStep <= 1) return;
        this._stepperStep--;
    }

    /**
     * On next
     */
    public override onNext() {
        if (this._stepperStep >= this._stepperMax) return;
        this._stepperStep++;
    }

    /**
     * On submit
     */
    protected async onSubmit() {
        if (!this.canSubmit()) {
            return;
        }

        await this.onSubmitConfirmed();
    }

    /**
     * On submit (After potential validation)
     */
    private async onSubmitConfirmed() {
        if (!this.canSubmit() || !this.taskType) {
            return;
        }

        const finalInputsMappings = await this.stripUnchangedSecretInputMappingsForUpdate();
        const finalTaskType = this.determineFinalTaskType();
        const parentTaskUUID = this.form.controls['parent_uuid'].value?.key;
        const toSubmit: CpmCTaskCreateApiSchema | CpmCTaskUpdateApiSchema = {
            parent_task_uuid: parentTaskUUID ?? null,
            name: this.form.controls['name']?.value,
            due_date: DateUtils.toLocalISOString(this.form.controls['due_date']?.value),
            start_date: DateUtils.toLocalISOString(this.form.controls['start_date']?.value),
            end_date: DateUtils.toLocalISOString(this.form.controls['end_date']?.value),
            brief: this.form.controls['brief']?.value,
            description: this.form.controls['description']?.value,
            depends_on: this.dependenciesForm.value ? this.dependenciesForm.value.map((d) => d.key!) : [],
            inputs_mapping: finalInputsMappings,
            outputs_mapping: this._outputsMappings
        };

        toSubmit.task_type = finalTaskType;

        const selectedOptions = this.form.controls['options'].value || [];

        // Set selected options to true
        selectedOptions.forEach((tag: string) =>
            Object.assign(toSubmit, TaskOptionsUtils.buildUpdatePayload(tag, true))
        );

        // Set previously selected but now unselected options to false (only in edit mode)
        if (!this.isCreationMode && this._item) {
            this.options.forEach((opt) => {
                if (!selectedOptions.includes(opt.tag) && opt.checked) {
                    Object.assign(toSubmit, TaskOptionsUtils.buildUpdatePayload(opt.tag, false));
                }
            });
        }

        if (finalTaskType === ECpmCTaskTypeApiEnum.SMART) {
            const selectedFunction = this.form.controls['function']?.value;
            if (selectedFunction) {
                const pluginId = selectedFunction.parent?.data?.id;
                const functionId = selectedFunction.data?.id;

                toSubmit.plugin_id = pluginId;
                toSubmit.plugin_function_id = functionId;
            }
        } else if (finalTaskType === ECpmCTaskTypeApiEnum.SIMPLE && !this.isCreationMode) {
            toSubmit.plugin_id = null;
            toSubmit.plugin_function_id = null;
            toSubmit.task_type = ECpmCTaskTypeApiEnum.SIMPLE;
        }

        await this.submitCU(
            null,
            CpmCTaskApiService.createTask,
            CpmCTaskApiService.updateTask,
            this._item?.uuid,
            toSubmit,
            (resultCRUD: CpmCTaskApiModel) => new NewEditTaskModalResult(resultCRUD)
        );
    }

    /**
     * Determine final task type based on function selection
     */
    private determineFinalTaskType(): ECpmCTaskTypeApiEnum {
        if (this.taskType !== ECpmCTaskTypeApiEnum.SMART && this.taskType !== ECpmCTaskTypeApiEnum.SIMPLE) {
            return this.taskType!;
        }

        const functionSelected = this.form.controls['function']?.value;
        return functionSelected ? ECpmCTaskTypeApiEnum.SMART : ECpmCTaskTypeApiEnum.SIMPLE;
    }

    private async stripUnchangedSecretInputMappingsForUpdate(): Promise<
        Record<string, CpmCTaskInputOutputMappingApiSchema>
    > {
        const rootSchema = this._selectedPluginFunction?.input_schema
            ? await this.resolveInputRootSchemaForSecretStrip(this._selectedPluginFunction.input_schema as JSONSchema7)
            : undefined;

        return JsonUtils.stripUnchangedSecretInputMappingsForUpdate(this._inputsMappings, {
            isCreationMode: this.isCreationMode,
            initialMappings: this._initialInputsMappings,
            rootSchema,
            resolveInitialSerializedValue: (mappingKey: string): string | undefined =>
                this.resolveInitialInputSerializedForComparison(mappingKey)
        });
    }

    /**
     * Resolves the initial serialized value for a given input mapping key for comparison purposes.
     */
    private resolveInitialInputSerializedForComparison(mappingKey: string): string | undefined {
        const initialMapping = this._initialInputsMappings?.[mappingKey];
        if (!initialMapping) {
            return undefined;
        }

        if (initialMapping.value_serialized !== undefined && initialMapping.value_serialized !== null) {
            return initialMapping.value_serialized;
        }

        if (!initialMapping.reference_expression) {
            return undefined;
        }

        const expressions = ReferenceExpressionUtils.parseExpressions(initialMapping.reference_expression, true);
        if (expressions.length !== 1) {
            return undefined;
        }

        const expression = expressions[0];
        const entry = this._objectStoreEntriesTransientCache?.getEntryForLevel(
            expression.sourceIdentifier as EObjectStoreEntriesLevel,
            expression.attributeName
        );

        return entry?.value ?? undefined;
    }

    /**
     * Resolves the root schema for the input, used for stripping secret input mappings.
     */
    private async resolveInputRootSchemaForSecretStrip(inputSchema: JSONSchema7): Promise<JSONSchema7 | undefined> {
        this._specificationAppDetails ??= (await firstValueFrom(
            JobsSpecificationCaching.getExecutorActiveSpecificationAppDetails('action')
        )) as unknown as CpmAEAllPluginSpecApiSchema;

        return {
            ...inputSchema,
            definitions: this._specificationAppDetails.type_definitions as Record<string, JSONSchema7>
        };
    }

    /**
     * Handles changes to the task's input/output.
     */
    protected onTaskIoChanged(event: ActionJobsFunctionIoEditorChangeEvent): void {
        if (event.side === 'input' && !this._functionDirty) {
            this._inputsDirty = true;
        }
    }

    /**
     * Retrieves the function associated with the current task.
     */
    private getTaskFunction(): TreeNode<CpmAEPluginSpecApiSchema | CpmAEPluginFunctionApiSchema> | undefined {
        if (!this._item?.plugin_id || !this._item.plugin_function_id) {
            return undefined;
        }

        const functionKey = ActionJobsUtils.functionsTreeKey(this._item.plugin_id, this._item.plugin_function_id);
        return TreeUtils.findTreeNodeByKey(this._functionsTree, functionKey);
    }

    // ===============================================================
    // ===============================================================

    /**
     * Retrieves the full confirmation message for the task change warning.
     * @param inputChanged - Indicates if the input has changed.
     * @param taskChanged - Indicates if the task has changed.
     * @param fromMilestone - Indicates if the change is from a milestone.
     * @returns The configuration for the confirmation modal.
     */
    protected getTaskChangeConfirmation(
        inputChanged?: boolean,
        taskChanged?: boolean,
        fromMilestone?: boolean
    ): ConfirmationModalConfig {
        const confirmationConfig = new ConfirmationModalConfig();
        confirmationConfig.title = 'Update warning';

        if (inputChanged) {
            confirmationConfig.message = `${this.taskInputChangeFirstSentence}\n${this.resetWarningSentence}`;
        } else if (taskChanged) {
            confirmationConfig.message = `${this.taskFunctionChangeFirstSentence}\n${this.resetWarningSentence}`;
        } else {
            confirmationConfig.message = this.resetWarningSentence;
        }
        if (fromMilestone) {
            confirmationConfig.message = `${confirmationConfig.message}\n${this.milestoneSentence}`;
        }
        confirmationConfig.message = `${confirmationConfig.message}\n${this.confirmationSentence}`;

        confirmationConfig.severity = 'warn';
        confirmationConfig.confirmationConfig.severity = 'danger';
        return confirmationConfig;
    }
}
